import React, { useMemo } from 'react';
import cx from 'classnames';
import {
  EmbedTreeNodeData,
  EmbedType,
} from '@mutiny-pkg/champaign-schemas/src/schemas/flexiblePages/flexiblePagesTreeNode';
import { champaignUrlDomain } from '@mutiny-pkg/config/src/domain';
import { hydrateRichTextVariables, renderRichTextToPlainString } from '../../richtext/ast';
import { transformToContainerQueries } from '../utils/transformBreakpointClasses';

type EmbedProps = EmbedTreeNodeData & {
  id: string;
  editorClassName?: string;
  overrideVariables?: Partial<Record<string, string>>;
  isEditor: boolean;
  isDoubleClickActive?: boolean;
};

const SafeUrlEmbed = ({
  resolvedContent,
  isDoubleClickActive,
}: Pick<EmbedProps, 'isDoubleClickActive'> & { resolvedContent: string }) => {
  const iframeClassName = `border-none w-full h-full ${isDoubleClickActive ? '' : 'pointer-events-none'}`;

  return <iframe src={resolvedContent} title="Embedded content" loading="lazy" className={iframeClassName} />;
};

const SafeContentEmbed = ({
  resolvedContent,
  isDoubleClickActive,
}: Pick<EmbedProps, 'isDoubleClickActive'> & { resolvedContent: string }) => {
  const wrappedContentSnippet = `<html style="height: 100%; width: 100%;">
<body style="margin: 0; padding: 0; height: 100%; width: 100%; overflow: hidden;">
${resolvedContent}</body></html>`;
  const src = `${champaignUrlDomain()}/render/iframes?srcdoc=${encodeURIComponent(wrappedContentSnippet || '')}`;
  const iframeClassName = `border-none w-full h-full ${isDoubleClickActive ? '' : 'pointer-events-none'}`;

  return <iframe src={src} title="Embedded content" loading="lazy" className={iframeClassName} />;
};

const ContentEmbed = ({
  resolvedContent,
  isEditor,
  isDoubleClickActive,
}: Pick<EmbedProps, 'isEditor' | 'isDoubleClickActive'> & { resolvedContent: string }) => {
  if (isEditor) {
    return <SafeContentEmbed resolvedContent={resolvedContent} isDoubleClickActive={isDoubleClickActive} />;
  }

  return <div style={{ width: '100%', height: '100%' }} dangerouslySetInnerHTML={{ __html: resolvedContent }} />;
};

const hydrateAndRenderContent = (
  content: EmbedProps['url'] | EmbedProps['codeSnippet'],
  overrideVariables: EmbedProps['overrideVariables'],
) => {
  const contentData = content || '';
  const hydratedContent =
    typeof contentData !== 'string' ? hydrateRichTextVariables(contentData, overrideVariables || {}) : contentData;
  return typeof hydratedContent !== 'string' ? renderRichTextToPlainString(hydratedContent) : hydratedContent;
};

export const Embed = React.forwardRef<HTMLDivElement, EmbedProps>((embedProps, ref) => {
  const {
    id,
    className,
    editorClassName,
    embedType,
    url,
    codeSnippet,
    overrideVariables,
    isEditor,
    isDoubleClickActive,
    ...props
  } = embedProps;

  const resolvedContent = useMemo(() => {
    if (embedType === EmbedType.URL && url) {
      return hydrateAndRenderContent(url, overrideVariables);
    }
    if (embedType === EmbedType.Code && codeSnippet) {
      return hydrateAndRenderContent(codeSnippet, overrideVariables);
    }
    return '';
  }, [embedType, url, codeSnippet, overrideVariables]);

  return (
    <div
      id={id}
      ref={ref}
      className={cx(transformToContainerQueries(className, { isEditor }), editorClassName)}
      {...props}
    >
      {embedType === EmbedType.URL ? (
        isEditor ? (
          <SafeUrlEmbed resolvedContent={resolvedContent} isDoubleClickActive={isDoubleClickActive} {...props} />
        ) : (
          <iframe
            src={resolvedContent}
            title="Embedded content"
            loading="lazy"
            style={{ border: 'none', width: '100%', height: '100%' }}
          />
        )
      ) : (
        <ContentEmbed
          resolvedContent={resolvedContent}
          isEditor={isEditor}
          isDoubleClickActive={isDoubleClickActive}
          {...props}
        />
      )}
    </div>
  );
});

Embed.displayName = 'Embed';
